'use client';

import { c as _c } from "react/compiler-runtime";
import 'swiper/css';
import clsx from 'clsx';
import { FC, memo, useRef, useState, useSyncExternalStore } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';
import { chunkArrayCyclic, getMaxWidthServerSnapshot, getMaxWidthSnapshot, getMaxWidthSubscribe } from '@/components/CasesChunkedSlider/helpers';
import { useReveal } from '@/components/CasesChunkedSlider/useReveal';
import ArrowNext from '@/components/shared/Icon/ArrowNext';
import ArrowPrev from '@/components/shared/Icon/ArrowPrev';
import { Typography } from '@/components/shared/Typography';
import useDevice from '@/hooks/useDevice';
import CaseSlide from './CaseSlide';
import css from './style.module.scss';
import { CaseSlideItem } from './types';
const DESKTOP_CHUNK_SIZE = 2;
interface Props {
  title: string;
  items: CaseSlideItem[];
  disableBreakpoint?: number;
  withOverlay?: boolean;
  hideDefaultCursor?: boolean;
  showTotalCount?: boolean;
  className?: string;
  setIsCustomCursorVisible?: (value: boolean) => void;
}
const CasesChunkedSlider: FC<Props> = t0 => {
  const $ = _c(26);
  const {
    title,
    items,
    disableBreakpoint,
    withOverlay,
    hideDefaultCursor,
    showTotalCount: t1,
    className,
    setIsCustomCursorVisible
  } = t0;
  const showTotalCount = t1 === undefined ? true : t1;
  const isDesktop = useDevice() === "desktop";
  const wrapperRef = useRef(null);
  const swiperRef = useRef(null);
  const [activeSlideRealIndex, setActiveSlideRealIndex] = useState(0);
  let t2;
  if ($[0] !== disableBreakpoint) {
    t2 = getMaxWidthSubscribe(disableBreakpoint);
    $[0] = disableBreakpoint;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== disableBreakpoint) {
    t3 = () => getMaxWidthSnapshot(disableBreakpoint);
    $[2] = disableBreakpoint;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isSliderDisabled = useSyncExternalStore(t2, t3, getMaxWidthServerSnapshot);
  const groupedSlides = isDesktop ? chunkArrayCyclic(items, DESKTOP_CHUNK_SIZE) : items.map(_temp);
  useReveal(wrapperRef);
  if (!items.length) {
    return null;
  }
  let t4;
  if ($[4] !== hideDefaultCursor || $[5] !== withOverlay) {
    t4 = {
      hideDefaultCursor,
      withOverlay
    };
    $[4] = hideDefaultCursor;
    $[5] = withOverlay;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const slideCardProps = t4;
  let t5;
  if ($[7] !== activeSlideRealIndex || $[8] !== setIsCustomCursorVisible || $[9] !== slideCardProps) {
    t5 = t6 => {
      const {
        chunk,
        index,
        width,
        withCustomCursor
      } = t6;
      return <div className={css.slideWrapper} onMouseEnter={withCustomCursor ? () => setIsCustomCursorVisible?.(true) : undefined} onMouseLeave={withCustomCursor ? () => setIsCustomCursorVisible?.(false) : undefined}>{chunk.map((slide, slideIndex) => <CaseSlide key={`${slide.id}-${slideIndex}`} data={slide} width={width} isVisible={activeSlideRealIndex === index} {...slideCardProps} />)}</div>;
    };
    $[7] = activeSlideRealIndex;
    $[8] = setIsCustomCursorVisible;
    $[9] = slideCardProps;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const renderChunk = t5;
  let t6;
  if ($[11] !== className) {
    t6 = clsx(css.wrapper, className);
    $[11] = className;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== items.length || $[14] !== showTotalCount) {
    t7 = showTotalCount ? <sup>{items.length}</sup> : null;
    $[13] = items.length;
    $[14] = showTotalCount;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7 || $[17] !== title) {
    t8 = <Typography variant="h4" tag="h2" className={css.title}>{title} {t7}</Typography>;
    $[16] = t7;
    $[17] = title;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = isDesktop && groupedSlides.length > 1 ? <div className={css.buttonWrapper}><button type="button" className={css.button} onClick={() => swiperRef.current?.slidePrev()}><ArrowPrev size={24} />Previous</button><button type="button" className={css.button} onClick={() => swiperRef.current?.slideNext()}>Next<ArrowNext size={24} /></button></div> : null;
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className={css.head}>{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = isSliderDisabled ? <div className={css.disabledSliderWrapper}>{items.map(slide_0 => <div key={slide_0.id} className={css.slideWrapper}><CaseSlide data={slide_0} width={100} {...slideCardProps} /></div>)}</div> : <div className={css.sliderWrapper}><Swiper loop={groupedSlides.length > 1} slidesPerView={isDesktop ? 1 : 1.18} speed={500} spaceBetween={isDesktop ? 16 : 20} onSlideChange={swiper => setActiveSlideRealIndex(swiper.realIndex)} onSwiper={swiper_0 => {
      swiperRef.current = swiper_0;
      setActiveSlideRealIndex(swiper_0.realIndex);
    }}>{groupedSlides.map((chunk_0, index_0) => <SwiperSlide key={index_0}>{renderChunk({
          chunk: chunk_0,
          index: index_0,
          width: isDesktop ? 50 : 100,
          withCustomCursor: isDesktop
        })}</SwiperSlide>)}</Swiper></div>;
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t6) {
    t12 = <section ref={wrapperRef} className={t6}>{t10}{t11}</section>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t6;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};
export default memo(CasesChunkedSlider);
function _temp(item) {
  return [item];
}